// ============================================================
// Billing Page
// ============================================================

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { CreditCard, Star, Zap, CheckCircle } from 'lucide-react';

export function BillingPage() {
    const { data: billingData } = useQuery({
        queryKey: ['billing'],
        queryFn: () => api.get('/billing/plan'),
    });

    const { data: usageData } = useQuery({
        queryKey: ['billing-usage'],
        queryFn: () => api.get('/billing/usage'),
    });

    const currentPlan = billingData?.data?.plan;
    const subscription = billingData?.data?.subscription;
    const plans = billingData?.data?.plans || [];
    const meters = usageData?.data?.meters || [];

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Billing</h1>
                    <p className="subtitle">Manage your subscription and usage</p>
                </div>
            </div>

            {/* Current Plan */}
            <div className="card mb-6" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.05))', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <div className="flex justify-between items-center">
                    <div>
                        <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: 4 }}>Current Plan</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            {currentPlan?.name || 'Free'}
                        </div>
                        {subscription && (
                            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 4 }}>
                                {subscription.status === 'active' ? '✅ Active' : subscription.status} · Renews {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : '—'}
                            </div>
                        )}
                    </div>
                    <Star size={40} color="#6366f1" style={{ opacity: 0.3 }} />
                </div>
            </div>

            {/* Plans */}
            <h2 style={{ fontWeight: 700, marginBottom: 16 }}>Available Plans</h2>
            <div className="card-grid card-grid-3 mb-6">
                {plans.map((plan: any) => (
                    <div key={plan.id} className="card" style={{
                        border: currentPlan?.id === plan.id ? '2px solid #6366f1' : undefined,
                        position: 'relative',
                    }}>
                        {currentPlan?.id === plan.id && (
                            <div style={{
                                position: 'absolute', top: -10, right: 16,
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                padding: '2px 12px', borderRadius: 20, fontSize: '11px', fontWeight: 700, color: 'white',
                            }}>CURRENT</div>
                        )}
                        <h3 style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>{plan.name}</h3>
                        <div style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: 16 }}>
                            {plan.price === '0' || plan.price === 0 ? 'Free' : `₹${plan.price}`}
                            {plan.price > 0 && <span style={{ fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>/mo</span>}
                        </div>
                        <div className="flex-col gap-3">
                            {Object.entries(plan.limits || {}).map(([key, val]) => (
                                <div key={key} className="flex items-center gap-2" style={{ fontSize: '13px', color: '#4b5563' }}>
                                    <CheckCircle size={14} color="#10b981" /> {key}: {String(val)}
                                </div>
                            ))}
                        </div>
                        {currentPlan?.id !== plan.id && (
                            <button className="btn btn-secondary w-full mt-4 btn-sm">
                                {(plan.price || 0) > (currentPlan?.price || 0) ? 'Upgrade' : 'Downgrade'}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Usage */}
            <h2 style={{ fontWeight: 700, marginBottom: 16 }}>Current Usage</h2>
            <div className="card">
                {meters.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>No usage tracked this period</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr><th>Metric</th><th>Count</th><th>Period</th></tr>
                        </thead>
                        <tbody>
                            {meters.map((m: any) => (
                                <tr key={m.id}>
                                    <td style={{ fontWeight: 500 }}>{m.metric}</td>
                                    <td>{m.count}</td>
                                    <td style={{ color: '#6b7280' }}>{m.period}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
